/**
 * Admin Role-Based Access Control (RBAC) system.
 *
 * Defines 5 admin roles with granular permissions:
 * - super_admin: Full access (wildcard)
 * - admin: Everything except account management
 * - content_manager: Content and moderation
 * - cs_agent: User support and basic monitoring
 * - viewer: Read-only access
 *
 * SQL to update Supabase constraint:
 * ```sql
 * ALTER TABLE admin_accounts DROP CONSTRAINT IF EXISTS admin_accounts_role_check;
 * ALTER TABLE admin_accounts ADD CONSTRAINT admin_accounts_role_check
 *   CHECK (role IN ('super_admin', 'admin', 'content_manager', 'cs_agent', 'viewer'));
 * ```
 */

export type AdminRole = 'super_admin' | 'admin' | 'content_manager' | 'cs_agent' | 'viewer'

export interface RolePermissions {
  label: string
  description: string
  permissions: string[]
}

export const ADMIN_ROLES: Record<AdminRole, RolePermissions> = {
  super_admin: {
    label: '최고 관리자',
    description: '모든 권한',
    permissions: ['*'],
  },
  admin: {
    label: '관리자',
    description: '계정 관리를 제외한 모든 권한',
    permissions: [
      'dashboard.view', 'users.view', 'users.edit', 'users.export',
      'moderation.view', 'moderation.delete',
      'content.view', 'content.edit',
      'subscriptions.view', 'subscriptions.manage',
      'notifications.view', 'notifications.send',
      'monitoring.view',
      'audit.view', 'audit.export',
      'auth_events.view',
      'reports.view', 'reports.manage',
      'feedback.view', 'feedback.reply',
      'app_config.view',
      'ab_test.view',
    ],
  },
  content_manager: {
    label: '콘텐츠 관리자',
    description: '콘텐츠 및 경험담 관리',
    permissions: [
      'dashboard.view',
      'moderation.view', 'moderation.delete',
      'content.view', 'content.edit',
      'notifications.view', 'notifications.send',
      'reports.view', 'reports.manage',
    ],
  },
  cs_agent: {
    label: 'CS 담당',
    description: '사용자 문의 및 기본 관리',
    permissions: [
      'dashboard.view',
      'users.view',
      'feedback.view', 'feedback.reply',
      'reports.view', 'reports.manage',
      'monitoring.view',
    ],
  },
  viewer: {
    label: '뷰어',
    description: '읽기 전용',
    permissions: [
      'dashboard.view', 'users.view', 'moderation.view',
      'content.view', 'monitoring.view', 'audit.view',
    ],
  },
}

/** All known roles in display order */
export const ALL_ROLES: AdminRole[] = ['super_admin', 'admin', 'content_manager', 'cs_agent', 'viewer']

/**
 * Check if a role has a specific permission.
 * super_admin has wildcard ('*') which grants all permissions.
 */
export function hasPermission(role: AdminRole, permission: string): boolean {
  const rolePerms = ADMIN_ROLES[role]
  if (!rolePerms) return false
  if (rolePerms.permissions.includes('*')) return true
  return rolePerms.permissions.includes(permission)
}

/**
 * Map admin routes to their required permissions.
 * '*' means only super_admin can access (via wildcard match).
 */
export const ROUTE_PERMISSIONS: Record<string, string> = {
  '/': 'dashboard.view',
  '/users': 'users.view',
  '/moderation': 'moderation.view',
  '/content': 'content.view',
  '/subscriptions': 'subscriptions.view',
  '/notifications': 'notifications.view',
  '/monitoring': 'monitoring.view',
  '/audit': 'audit.view',
  '/auth-events': 'auth_events.view',
  '/accounts': '*',  // super_admin only
  '/reports': 'reports.view',
  '/feedback': 'feedback.view',
  '/app-config': 'app_config.view',
  '/ab-test': 'ab_test.view',
}

/** Role badge styling */
export const ROLE_STYLE: Record<AdminRole, { color: string; bg: string }> = {
  super_admin: { color: '#6B3FD4', bg: '#F3EEFF' },
  admin: { color: '#1D72E8', bg: '#EBF3FF' },
  content_manager: { color: '#0D9488', bg: '#E6FAF8' },
  cs_agent: { color: '#D97706', bg: '#FEF7E6' },
  viewer: { color: '#6B7280', bg: '#F3F4F6' },
}
